import React, { useState, useEffect, useCallback } from 'react';
import { User } from '../../types';
import { casinoService, CasinoInfo, BlackjackGame } from '../../services/casinoService';
import { useError } from '../../contexts/ErrorContext';

interface BlackjackProps {
  user: User;
  onUpdate: () => void;
}

const Blackjack: React.FC<BlackjackProps> = ({ user, onUpdate }) => {
  const [casinos, setCasinos] = useState<CasinoInfo[]>([]);
  const [selectedCasino, setSelectedCasino] = useState<CasinoInfo | null>(null);
  const [betAmount, setBetAmount] = useState<number>(100);
  const [currentGame, setCurrentGame] = useState<BlackjackGame | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingCasinos, setLoadingCasinos] = useState(true);
  const { showError, showSuccess } = useError();

  const loadCasinos = useCallback(async () => {
    setLoadingCasinos(true);
    try {
      const result = await casinoService.getCasinosByLocation(user.location);
      if (result.success) {
        const blackjackCasinos = result.casinos.filter((casino: CasinoInfo) => casino.type === 'blackjack');
        setCasinos(blackjackCasinos);
        if (blackjackCasinos.length > 0) {
          setSelectedCasino(blackjackCasinos[0]);
        }
      }
    } catch (error) {
      console.error('Error loading casinos:', error);
      showError('Failed to load casinos. Please try again.');
    } finally {
      setLoadingCasinos(false);
    }
  }, [user.location, showError]);

  useEffect(() => {
    loadCasinos();
  }, [loadCasinos]);

  const startGame = async () => {
    if (!selectedCasino) {
      showError('Please select a casino');
      return;
    }

    setLoading(true);
    try {
      const result = await casinoService.startBlackjackGame(selectedCasino.id, betAmount);
      if (result.success) {
        setCurrentGame(result.game);
        onUpdate(); // Refresh user money
        showSuccess('Game started!');
      } else {
        showError(result.message || 'Failed to start game');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error starting game';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const hit = async () => {
    if (!currentGame || !selectedCasino) return;

    setLoading(true);
    try {
      const result = await casinoService.hit(currentGame.gameId, selectedCasino.id);
      if (result.success) {
        setCurrentGame(result.game);
        if (result.game.gameState === 'complete') {
          onUpdate(); // Refresh user money
          showSuccess('Game complete!');
        }
      } else {
        showError(result.message || 'Failed to hit');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error hitting';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const stand = async () => {
    if (!currentGame || !selectedCasino) return;

    setLoading(true);
    try {
      const result = await casinoService.stand(currentGame.gameId, selectedCasino.id);
      if (result.success) {
        setCurrentGame(result.game);
        if (result.game.gameState === 'complete') {
          onUpdate(); // Refresh user money
          showSuccess('Game complete!');
        }
      } else {
        showError(result.message || 'Failed to stand');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error standing';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const double = async () => {
    if (!currentGame || !selectedCasino) return;

    setLoading(true);
    try {
      const result = await casinoService.double(currentGame.gameId, selectedCasino.id);
      if (result.success) {
        setCurrentGame(result.game);
        if (result.game.gameState === 'complete') {
          onUpdate(); // Refresh user money
          showSuccess('Game complete!');
        }
      } else {
        showError(result.message || 'Failed to double');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error doubling';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const newGame = () => {
    setCurrentGame(null);
  };

  const getCardDisplay = (card: number): string => {
    if (card === 1) return 'A';
    if (card === 11) return 'J';
    if (card === 12) return 'Q';
    if (card === 13) return 'K';
    return card.toString();
  };

  const getCardSuit = (card: number): string => {
    const suits = ['♠', '♥', '♦', '♣'];
    return suits[Math.floor((card - 1) / 13)];
  };

  const getCardColor = (card: number): string => {
    const suit = Math.floor((card - 1) / 13);
    return suit === 1 || suit === 2 ? 'red' : 'black';
  };

  const getGameResult = (): string => {
    if (!currentGame || currentGame.gameState !== 'complete') return '';

    const playerBlackjack = currentGame.playerTotal === 21 && currentGame.playerHand.length === 2;
    const dealerBlackjack = currentGame.dealerTotal === 21 && currentGame.dealerHand.length === 2;

    if (playerBlackjack && !dealerBlackjack) return 'Blackjack! You win!';
    if (dealerBlackjack && !playerBlackjack) return 'Dealer Blackjack! You lose!';
    if (playerBlackjack && dealerBlackjack) return 'Push! Both have Blackjack!';
    if (currentGame.playerTotal > 21) return 'Bust! You lose!';
    if (currentGame.dealerTotal > 21) return 'Dealer Bust! You win!';
    if (currentGame.playerTotal > currentGame.dealerTotal) return 'You win!';
    if (currentGame.playerTotal < currentGame.dealerTotal) return 'Dealer wins!';
    return 'Push!';
  };

  return (
    <div className="blackjack-page">
      <h2 className="page-title">Blackjack</h2>
      
      <div className="blackjack-content">
        {/* Casino Selection */}
        <div className="casino-selection">
          <h3>Available Blackjack Tables</h3>
          {loadingCasinos ? (
            <div className="loading-casinos">
              <p>Loading casinos in {user.location}...</p>
            </div>
          ) : casinos.length > 0 ? (
            <div className="casino-list">
              {casinos.map((casino) => (
                <div 
                  key={casino.id}
                  className={`casino-card ${selectedCasino?.id === casino.id ? 'selected' : ''}`}
                  onClick={() => setSelectedCasino(casino)}
                >
                  <h4>{casino.name}</h4>
                  <p>Owner: {casino.ownerName || 'None'}</p>
                  <p>Max Bet: ${casino.maxBet.toLocaleString()}</p>
                  <p>House Balance: ${casino.currentBalance.toLocaleString()}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-casinos">
              <p>No Blackjack tables available in {user.location}.</p>
              <p>Try traveling to a different location!</p>
            </div>
          )}
        </div>

        {/* Game Interface */}
        {!currentGame ? (
          /* Betting Section */
          selectedCasino && (
            <div className="betting-section">
              <h3>Place Your Bet</h3>
              <div className="bet-controls">
                <label>
                  Bet Amount: $
                  <input
                    type="number"
                    value={betAmount}
                    onChange={(e) => setBetAmount(Number(e.target.value))}
                    min="1"
                    max={selectedCasino.maxBet}
                    step="1"
                  />
                </label>
                <p>Max Bet: ${selectedCasino.maxBet.toLocaleString()}</p>
                <p>Your Money: ${user.money.toLocaleString()}</p>
                <button
                  className="play-button"
                  onClick={startGame}
                  disabled={loading || betAmount > user.money || betAmount > selectedCasino.maxBet}
                >
                  {loading ? 'Starting...' : 'Start Game'}
                </button>
              </div>
            </div>
          )
        ) : (
          /* Game Play Section */
          <div className="game-play-section">
            <h3>Blackjack Game</h3>
            
            {/* Dealer's Hand */}
            <div className="hand-section">
              <h4>Dealer's Hand ({currentGame.dealerTotal})</h4>
              <div className="cards">
                {currentGame.dealerHand.map((card, index) => (
                  <div 
                    key={index} 
                    className="card"
                    style={{ color: getCardColor(card) }}
                  >
                    {getCardDisplay(card)}{getCardSuit(card)}
                  </div>
                ))}
              </div>
            </div>

            {/* Player's Hand */}
            <div className="hand-section">
              <h4>Your Hand ({currentGame.playerTotal})</h4>
              <div className="cards">
                {currentGame.playerHand.map((card, index) => (
                  <div 
                    key={index} 
                    className="card"
                    style={{ color: getCardColor(card) }}
                  >
                    {getCardDisplay(card)}{getCardSuit(card)}
                  </div>
                ))}
              </div>
            </div>

            {/* Game Actions */}
            {currentGame.gameState === 'player-turn' && (
              <div className="game-actions">
                <button
                  className="action-button hit"
                  onClick={hit}
                  disabled={loading || !currentGame.canHit}
                >
                  Hit
                </button>
                <button
                  className="action-button stand"
                  onClick={stand}
                  disabled={loading || !currentGame.canStand}
                >
                  Stand
                </button>
                <button
                  className="action-button double"
                  onClick={double}
                  disabled={loading || !currentGame.canDouble}
                >
                  Double
                </button>
              </div>
            )}

            {/* Game Result */}
            {currentGame.gameState === 'complete' && (
              <div className="game-result">
                <h4 className="result-message">{getGameResult()}</h4>
                <p>Bet: ${currentGame.betAmount.toLocaleString()}</p>
                {currentGame.isDoubled && <p className="doubled">Doubled Down!</p>}
                <button className="new-game-button" onClick={newGame}>
                  New Game
                </button>
              </div>
            )}

            {/* Game Info */}
            <div className="game-info">
              <p>Bet: ${currentGame.betAmount.toLocaleString()}</p>
              <p>Your Money: ${user.money.toLocaleString()}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blackjack;
